import { z } from 'zod';

const IdSchema = z.string().min(1).max(160);
const RefSchema = z.string().min(1).max(240);
const IsoDateTimeSchema = z.string().datetime({ offset: true });

export const SdkWorkSurfaceSubjectScopeSchema = z.enum(['platform', 'tenant', 'network', 'user']);
export type SdkWorkSurfaceSubjectScope = z.infer<typeof SdkWorkSurfaceSubjectScopeSchema>;

export const SdkWorkSurfaceSubjectRefSchema = z.object({
  scope: SdkWorkSurfaceSubjectScopeSchema,
  tenantId: IdSchema.optional(),
  moduleRef: RefSchema.optional(),
  entityType: RefSchema.optional(),
  entityId: IdSchema.optional(),
  fieldPath: z.string().min(1).max(240).optional(),
  dataRef: RefSchema.optional(),
  externalRef: z.object({
    sourceKey: RefSchema,
    externalType: RefSchema,
    externalId: z.string().min(1).max(240),
  }).optional(),
  runRef: z.object({
    runId: IdSchema.optional(),
    goalId: IdSchema.optional(),
    stepId: IdSchema.optional(),
  }).optional(),
}).superRefine((subject, ctx) => {
  if (subject.scope === 'tenant' && !subject.tenantId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tenantId'],
      message: 'tenant scoped subjects require tenantId.',
    });
  }
  if (subject.scope === 'platform' && subject.tenantId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tenantId'],
      message: 'platform scoped subjects must not carry tenant business tenantId.',
    });
  }
  if (subject.fieldPath && (!subject.entityType || !subject.entityId)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['fieldPath'],
      message: 'fieldPath requires entityType and entityId.',
    });
  }
  if (!subject.entityType && !subject.dataRef && !subject.externalRef && !subject.runRef) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['entityType'],
      message: 'subject requires at least entityType, dataRef, externalRef, or runRef.',
    });
  }
});
export type SdkWorkSurfaceSubjectRef = z.infer<typeof SdkWorkSurfaceSubjectRefSchema>;

export const SdkWorkSurfaceActorRefSchema = z.object({
  actorType: z.enum(['user', 'role', 'team', 'agent', 'external-party', 'system']),
  actorId: IdSchema,
  displayName: z.string().min(1).max(160).optional(),
});
export type SdkWorkSurfaceActorRef = z.infer<typeof SdkWorkSurfaceActorRefSchema>;

export const SdkWorkSurfaceEvidenceRefSchema = z.object({
  evidenceType: z.enum(['document', 'photo', 'signature', 'message', 'audit', 'external-ref', 'run-output']),
  ref: RefSchema,
  label: z.string().min(1).max(160).optional(),
  metadata: z.record(z.unknown()).default({}),
});
export type SdkWorkSurfaceEvidenceRef = z.infer<typeof SdkWorkSurfaceEvidenceRefSchema>;

const WorkSurfaceBaseSchema = z.object({
  subjectRef: SdkWorkSurfaceSubjectRefSchema,
  title: z.string().min(1).max(240),
  description: z.string().max(2000).optional(),
  priority: z.enum(['P0', 'P1', 'P2', 'P3']).default('P2'),
  contextRef: RefSchema.optional(),
  actionRef: RefSchema.optional(),
  createdBy: SdkWorkSurfaceActorRefSchema,
  assignedTo: SdkWorkSurfaceActorRefSchema.optional(),
  evidenceRefs: z.array(SdkWorkSurfaceEvidenceRefSchema).default([]),
  tags: z.array(z.string().min(1).max(80)).default([]),
  versionTag: z.enum(['final', 'experimental']).default('final'),
  runId: IdSchema.optional(),
  idempotencyKey: IdSchema.optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema.optional(),
});

export const SdkWorkItemKindSchema = z.enum(['task', 'action', 'checklist', 'approval']);
export type SdkWorkItemKind = z.infer<typeof SdkWorkItemKindSchema>;

export const SdkWorkItemSchema = WorkSurfaceBaseSchema.extend({
  workItemId: IdSchema,
  kind: SdkWorkItemKindSchema,
  status: z.enum(['draft', 'open', 'in_progress', 'waiting_approval', 'blocked', 'completed', 'cancelled']),
  dueAt: IsoDateTimeSchema.optional(),
  checklistItems: z.array(z.object({
    itemId: IdSchema,
    label: z.string().min(1).max(240),
    required: z.boolean().default(true),
    status: z.enum(['pending', 'passed', 'failed', 'waived']).default('pending'),
  })).default([]),
  approval: z.object({
    approvalPolicyRef: RefSchema,
    requestedActionRef: RefSchema,
    decision: z.enum(['pending', 'approved', 'rejected', 'request_change']).default('pending'),
    decidedBy: SdkWorkSurfaceActorRefSchema.optional(),
    decidedAt: IsoDateTimeSchema.optional(),
    reason: z.string().max(1000).optional(),
  }).optional(),
}).superRefine((item, ctx) => {
  if (item.kind === 'checklist' && item.checklistItems.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['checklistItems'],
      message: 'checklist work items require at least one checklist item.',
    });
  }
  if (item.kind === 'approval' && !item.approval) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['approval'],
      message: 'approval work items require approval metadata.',
    });
  }
});
export type SdkWorkItem = z.infer<typeof SdkWorkItemSchema>;

export const SdkSignalSchema = WorkSurfaceBaseSchema.extend({
  signalId: IdSchema,
  signalType: z.enum(['alert', 'risk', 'exception', 'operational-signal']),
  severity: z.enum(['low', 'medium', 'high', 'critical']).default('medium'),
  status: z.enum(['open', 'acknowledged', 'resolved', 'dismissed']).default('open'),
  sourceRef: RefSchema,
  recommendedActionRefs: z.array(RefSchema).default([]),
  convertedWorkItemId: IdSchema.optional(),
  acknowledgedBy: SdkWorkSurfaceActorRefSchema.optional(),
  acknowledgedAt: IsoDateTimeSchema.optional(),
  resolvedBy: SdkWorkSurfaceActorRefSchema.optional(),
  resolvedAt: IsoDateTimeSchema.optional(),
});
export type SdkSignal = z.infer<typeof SdkSignalSchema>;

export const SdkScheduleItemSchema = WorkSurfaceBaseSchema.extend({
  scheduleItemId: IdSchema,
  scheduleType: z.enum(['event', 'reminder', 'automation']),
  status: z.enum(['scheduled', 'triggered', 'completed', 'cancelled', 'expired']).default('scheduled'),
  startsAt: IsoDateTimeSchema,
  endsAt: IsoDateTimeSchema.optional(),
  timezone: z.string().min(1).max(80).default('UTC'),
  recurrenceRef: RefSchema.optional(),
  reminderPolicyRef: RefSchema.optional(),
});
export type SdkScheduleItem = z.infer<typeof SdkScheduleItemSchema>;

export const SdkTopicThreadSchema = WorkSurfaceBaseSchema.extend({
  threadId: IdSchema,
  threadType: z.enum(['entity', 'topic', 'share', 'network', 'support']),
  status: z.enum(['open', 'resolved', 'archived']).default('open'),
  visibility: z.enum(['private', 'tenant', 'network', 'share-view']).default('tenant'),
  participantRefs: z.array(SdkWorkSurfaceActorRefSchema).default([]),
  messageCount: z.number().int().nonnegative().default(0),
  lastMessageAt: IsoDateTimeSchema.optional(),
});
export type SdkTopicThread = z.infer<typeof SdkTopicThreadSchema>;

export const SdkObjectLockSchema = z.object({
  lockId: IdSchema,
  subjectRef: SdkWorkSurfaceSubjectRefSchema,
  lockType: z.enum(['edit', 'operation', 'approval', 'external_sync', 'financial_posting']),
  status: z.enum(['locked', 'released', 'expired']),
  ownerRef: SdkWorkSurfaceActorRefSchema,
  reason: z.string().min(1).max(1000),
  acquiredAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema.optional(),
  releasedAt: IsoDateTimeSchema.optional(),
  runId: IdSchema.optional(),
  versionTag: z.enum(['final', 'experimental']).default('final'),
}).superRefine((lock, ctx) => {
  if (lock.status === 'released' && !lock.releasedAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['releasedAt'],
      message: 'released locks require releasedAt.',
    });
  }
});
export type SdkObjectLock = z.infer<typeof SdkObjectLockSchema>;

export const SdkShareViewSchema = z.object({
  shareViewId: IdSchema,
  subjectRef: SdkWorkSurfaceSubjectRefSchema,
  accessMode: z.enum(['public-token', 'invited', 'email-otp']).default('email-otp'),
  status: z.enum(['active', 'revoked', 'expired']).default('active'),
  visibleFieldPolicyRef: RefSchema,
  allowedActionPolicyRef: RefSchema,
  topicThreadId: IdSchema.optional(),
  tokenRef: RefSchema.optional(),
  maxViews: z.number().int().positive().optional(),
  viewCount: z.number().int().nonnegative().default(0),
  expiresAt: IsoDateTimeSchema.optional(),
  createdBy: SdkWorkSurfaceActorRefSchema,
  createdAt: IsoDateTimeSchema,
  versionTag: z.enum(['final', 'experimental']).default('final'),
});
export type SdkShareView = z.infer<typeof SdkShareViewSchema>;

export const SdkWorkspaceBookmarkTargetTypeSchema = z.enum([
  'route',
  'entity',
  'entity_view',
  'report',
  'work_item',
  'signal',
  'action',
  'external_link',
]);
export type SdkWorkspaceBookmarkTargetType = z.infer<typeof SdkWorkspaceBookmarkTargetTypeSchema>;

export const SdkWorkspaceBookmarkSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  userId: IdSchema,
  title: z.string().min(1).max(240),
  targetType: SdkWorkspaceBookmarkTargetTypeSchema,
  moduleRef: RefSchema.optional(),
  route: z.string().min(1).max(2000).optional(),
  subjectRef: SdkWorkSurfaceSubjectRefSchema.optional(),
  viewRef: RefSchema.optional(),
  actionRef: RefSchema.optional(),
  icon: z.string().min(1).max(80).optional(),
  color: z.string().min(1).max(40).optional(),
  group: z.string().min(1).max(120).optional(),
  sortOrder: z.number().int().min(0).max(100000).optional(),
  isPinned: z.boolean().default(false),
  openMode: z.enum(['same_tab', 'new_tab', 'side_panel']).default('same_tab'),
  metadata: z.record(z.unknown()).default({}),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type SdkWorkspaceBookmark = z.infer<typeof SdkWorkspaceBookmarkSchema>;

export const SdkWorkspaceRecentSchema = z.object({
  id: IdSchema,
  tenantId: IdSchema,
  userId: IdSchema,
  dedupeKey: z.string().min(1).max(240),
  title: z.string().min(1).max(240),
  moduleRef: RefSchema.optional(),
  route: z.string().min(1).max(2000).optional(),
  subjectRef: SdkWorkSurfaceSubjectRefSchema.optional(),
  icon: z.string().min(1).max(80).optional(),
  color: z.string().min(1).max(40).optional(),
  openCount: z.number().int().nonnegative().default(1),
  metadata: z.record(z.unknown()).default({}),
  lastOpenedAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type SdkWorkspaceRecent = z.infer<typeof SdkWorkspaceRecentSchema>;

export const SdkWorkSurfaceCapabilitySchema = z.object({
  capabilityId: IdSchema,
  contractRef: RefSchema,
  supportedPrimitives: z.array(z.enum([
    'work-item',
    'signal',
    'schedule-item',
    'topic-thread',
    'object-lock',
    'share-view',
  ])).min(1),
  requiredPolicyRefs: z.array(RefSchema).default([]),
  supportedSurfaceRefs: z.array(z.enum(['web', 'pwa', 'extension', 'execution-edge', 'ai-agent'])).min(1),
  requiresAudit: z.boolean().default(true),
  requiresSubjectRef: z.literal(true).default(true),
});
export type SdkWorkSurfaceCapability = z.infer<typeof SdkWorkSurfaceCapabilitySchema>;

export const SdkWorkSurfaceAddonManifestSchema = z.object({
  addonId: IdSchema,
  name: z.string().min(1).max(160),
  version: z.string().min(1).max(80),
  capability: SdkWorkSurfaceCapabilitySchema,
  sdkApiRefs: z.array(RefSchema).min(1),
  rolloutGateRefs: z.array(RefSchema).min(1),
});
export type SdkWorkSurfaceAddonManifest = z.infer<typeof SdkWorkSurfaceAddonManifestSchema>;

export function parseWorkSurfaceAddonManifest(input: unknown): SdkWorkSurfaceAddonManifest {
  return SdkWorkSurfaceAddonManifestSchema.parse(input);
}

export function validateWorkSurfaceAddonManifest(input: unknown): {
  ok: true;
  manifest: SdkWorkSurfaceAddonManifest;
} | {
  ok: false;
  issues: string[];
} {
  const parsed = SdkWorkSurfaceAddonManifestSchema.safeParse(input);
  if (parsed.success) return { ok: true, manifest: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`),
  };
}
